import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Section Actions (collapse / expand / hide-empty)
// ---------------------------------------------------------------------------

type SectionActionsContextType = {
    collapseSignal: number;
    expandSignal: number;
    expandSectionSignal: number;
    expandSectionTitle: string;
    hideEmptyFields: boolean;
    sectionsCollapsed: boolean;
    collapseAll: () => void;
    expandAll: () => void;
    toggleHideEmptyFields: () => void;
    requestExpandSection: (title: string) => void;
};

const defaultContext: SectionActionsContextType = {
    collapseSignal: 0,
    expandSignal: 0,
    expandSectionSignal: 0,
    expandSectionTitle: '',
    hideEmptyFields: false,
    sectionsCollapsed: false,
    collapseAll: () => {},
    expandAll: () => {},
    toggleHideEmptyFields: () => {},
    requestExpandSection: () => {},
};

const SectionActionsContext = createContext<SectionActionsContextType>(defaultContext);

export const SectionActionsProvider = ({ children }: { children: React.ReactNode }) => {
    const [collapseSignal, setCollapseSignal] = useState(0);
    const [expandSignal, setExpandSignal] = useState(0);
    const [expandSectionSignal, setExpandSectionSignal] = useState(0);
    const [expandSectionTitle, setExpandSectionTitle] = useState('');
    const [hideEmptyFields, setHideEmptyFields] = useState(false);
    const [sectionsCollapsed, setSectionsCollapsed] = useState(false);

    const collapseAll = useCallback(() => {
        setCollapseSignal((s) => s + 1);
        setSectionsCollapsed(true);
    }, []);
    const expandAll = useCallback(() => {
        setExpandSignal((s) => s + 1);
        setSectionsCollapsed(false);
    }, []);
    const toggleHideEmptyFields = useCallback(() => setHideEmptyFields((h) => !h), []);
    const requestExpandSection = useCallback((title: string) => {
        setExpandSectionTitle(title);
        setExpandSectionSignal((s) => s + 1);
    }, []);

    const value = useMemo(
        () => ({
            collapseSignal,
            expandSignal,
            expandSectionSignal,
            expandSectionTitle,
            hideEmptyFields,
            sectionsCollapsed,
            collapseAll,
            expandAll,
            toggleHideEmptyFields,
            requestExpandSection,
        }),
        [
            collapseSignal,
            expandSignal,
            expandSectionSignal,
            expandSectionTitle,
            hideEmptyFields,
            sectionsCollapsed,
            collapseAll,
            expandAll,
            toggleHideEmptyFields,
            requestExpandSection,
        ]
    );

    return <SectionActionsContext.Provider value={value}>{children}</SectionActionsContext.Provider>;
};

export const useSectionActions = () => useContext(SectionActionsContext);

// ---------------------------------------------------------------------------
// Section Title – provided by <Section> so children know which section they're in
// ---------------------------------------------------------------------------

const SectionTitleContext = createContext<string>('');
export const SectionTitleProvider = SectionTitleContext.Provider;
export const useSectionTitle = () => useContext(SectionTitleContext);

// ---------------------------------------------------------------------------
// Highlighted (required-empty) fields tracking
// ---------------------------------------------------------------------------
// Split into Dispatch (stable callbacks — consumed by InputWrapper) and
// State (counts per section — consumed by ContentMenu / SectionLayout)
// so that updating the counts does NOT re-render every InputWrapper.

type HighlightedFieldsDispatchType = {
    registerHighlightedField: (fieldName: string, sectionTitle: string) => void;
    unregisterHighlightedField: (fieldName: string) => void;
};

const HighlightedFieldsDispatchContext = createContext<HighlightedFieldsDispatchType>({
    registerHighlightedField: () => {},
    unregisterHighlightedField: () => {},
});

const HighlightedFieldsStateContext = createContext<Record<string, number>>({});

export const useHighlightedFieldsDispatch = () => useContext(HighlightedFieldsDispatchContext);
export const useHighlightedFieldsCounts = () => useContext(HighlightedFieldsStateContext);

export const HighlightedFieldsProvider = ({ children }: { children: React.ReactNode }) => {
    const fieldsRef = useRef<Record<string, string>>({}); // fieldName → sectionTitle
    const [counts, setCounts] = useState<Record<string, number>>({});

    const recomputeCounts = useCallback(() => {
        const next: Record<string, number> = {};
        for (const sectionTitle of Object.values(fieldsRef.current)) {
            next[sectionTitle] = (next[sectionTitle] ?? 0) + 1;
        }
        setCounts(next);
    }, []);

    const registerHighlightedField = useCallback(
        (fieldName: string, sectionTitle: string) => {
            if (fieldsRef.current[fieldName] === sectionTitle) return;
            fieldsRef.current[fieldName] = sectionTitle;
            recomputeCounts();
        },
        [recomputeCounts]
    );

    const unregisterHighlightedField = useCallback(
        (fieldName: string) => {
            if (!(fieldName in fieldsRef.current)) return;
            delete fieldsRef.current[fieldName];
            recomputeCounts();
        },
        [recomputeCounts]
    );

    const dispatch = useMemo(
        () => ({ registerHighlightedField, unregisterHighlightedField }),
        [registerHighlightedField, unregisterHighlightedField]
    );

    return (
        <HighlightedFieldsDispatchContext.Provider value={dispatch}>
            <HighlightedFieldsStateContext.Provider value={counts}>{children}</HighlightedFieldsStateContext.Provider>
        </HighlightedFieldsDispatchContext.Provider>
    );
};
